import { DollarSign, Euro, PoundSterling, JapaneseYen, SwissFranc, type LucideProps } from 'lucide-react';
import { useDisplayPreferences } from '../hooks/useDisplayPreferences';

/**
 * Maps currency codes to their corresponding Lucide icons.
 * For currencies without specific icons, falls back to DollarSign.
 */
const CURRENCY_ICONS: Record<string, React.ComponentType<LucideProps>> = {
  // Dollar-based currencies
  USD: DollarSign,
  CAD: DollarSign,
  AUD: DollarSign,
  NZD: DollarSign,
  MXN: DollarSign,
  // European currencies
  EUR: Euro,
  GBP: PoundSterling,
  CHF: SwissFranc,
  // Asian currencies
  JPY: JapaneseYen,
  CNY: JapaneseYen, // Uses same yen symbol
};

interface CurrencyIconProps extends Omit<LucideProps, 'ref'> {
  /** Optional currency code override. If not provided, uses user's preference. */
  currencyCode?: string;
}

/**
 * Renders a currency icon based on the user's currency preference or an override.
 * Automatically selects the appropriate Lucide icon for the currency.
 */
export function CurrencyIcon({ currencyCode, ...props }: CurrencyIconProps) {
  const { preferences } = useDisplayPreferences();
  
  const code = currencyCode || preferences.currency;
  const IconComponent = CURRENCY_ICONS[code] || DollarSign;
  
  return <IconComponent {...props} />;
}

export default CurrencyIcon;
